import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import {
    makeSleKey,
    makeSleChildKey,
    normalizeSleRatingForUi,
    normalizeSleRatingForApi,
    parseSleUiKey
} from "../utils/teacher/SleBatchUtils";

/**
 * Batch SLE ratings for GROUPS view.
 *
 * Responsibilities:
 * - Fetch SLE definitions for the school/year (same list as Homeroom).
 * - Fetch SLE ratings for the selected homeroom + shared groups (via groupInfoDto).
 * - Track:
 *   - valuesByStudent: { [gradingProfileStudentId]: { [uiKey]: rating } }
 *   - sleCommentsByStudent: { [gradingProfileStudentId]: { key, comment } }
 * - Build an update payload and save SLE ratings for all selected groups.
 *
 */
export default function useGroupSleRatings({
    enabled,
    groupInfoDto,
    schoolYearKey,
    profileLocationKey,
    token,
    userGuid
}) {
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(false);
    const [groupRows, setGroupRows] = useState([]);
    const [schoolLevelExpectations, setSchoolLevelExpectations] = useState([]);
    const [sleCommentsByStudent, setSleCommentsByStudent] = useState({});
    const [valuesByStudent, setValuesByStudent] = useState({}); // { [gpsId]: { [uiKey]: rating } }
    // Ignore stale getSleGroupData responses after the selected groups change.
    const groupFetchKeyRef = useRef(null);
    // Join overlapping saves (Save + leave-flush / group switch) so two PUTs
    // with key: null cannot CREATE duplicate SLE comments.
    const saveInFlightRef = useRef(null);

    // Map SLE child to parent SLE
    const childToParentMap = useMemo(() => {
        const map = {};
        for (const sle of schoolLevelExpectations || []) {
            for (const child of sle.sleChildList || []) {
                map[child.key] = sle.key;
            }
        }
        return map;
    }, [schoolLevelExpectations]);

    // Helper: hydrate local state from the group SLE payload.
    // includeComments defaults true (fetch / replace). Pass false after a ratings
    // save when comments were already applied from updateSleRatingCommentsGroup —
    // the ratings response still carries the pre-save studentSleCommentDto and
    // would wipe the newly saved text.
    const hydrateFromPayload = useCallback((payload, { includeComments = true } = {}) => {
        const nextValues = {};
        const nextComments = {};

        for (const group of payload || []) {
            for (const student of group.groupStudentSles || []) {
                const gpsId = Number(student.gradingProfileStudentId);
                const existingMap = nextValues[gpsId] || {};
                const map = { ...existingMap };

                for (const r of student.sleRatings || []) {
                    const uiKey = r.sleChildId == null ? makeSleKey(r.sleId) : makeSleChildKey(r.sleChildId);
                    map[uiKey] = normalizeSleRatingForUi(r.rating);
                }

                nextValues[gpsId] = map;

                if (includeComments && student.studentSleCommentDto) {
                    nextComments[gpsId] = {
                        key: Number(student.studentSleCommentDto.key),
                        comment: student.studentSleCommentDto.comment || ""
                    };
                }
            }
        }

        setValuesByStudent(nextValues);
        if (includeComments) {
            // Replace (do not merge with prev). Prev-wins left comments from a
            // prior group selection on the wrong students after a group switch.
            setSleCommentsByStudent(nextComments);
        }
    }, []);

    // Fetch SLE list for location/year (same as homeroom)
    useEffect(() => {
        if (!enabled || !schoolYearKey || !profileLocationKey || !token) return;

        setLoading(true);
        ElementaryProgressReportDao({
            action: "getSchoolLevelExpectations",
            schoolYearKey,
            locationKey: profileLocationKey,
            token
        })
            .then((res) => {
                if (res?.data?.payload) setSchoolLevelExpectations(res.data.payload);
            })
            .catch((error) => {
                console.error("Failed to load School Level Expectations (groups)", error);
                toast.error("There was a problem loading School Level Expectations. Please try again later.");
            })
            .finally(() => setLoading(false));
    }, [enabled, schoolYearKey, profileLocationKey, token]);

    // Fetch GROUP SLE ratings once SLE is enabled and we have a groupInfoDto
    useEffect(() => {
        if (!enabled || !groupInfoDto || !token) return;

        const fetchKey = JSON.stringify(groupInfoDto);
        groupFetchKeyRef.current = fetchKey;
        // Clear immediately so a slow previous selection cannot linger in the UI
        // while the new fetch is in flight (replace hydrate alone is not enough if
        // the old response arrives after the new selection).
        setGroupRows([]);
        setValuesByStudent({});
        setSleCommentsByStudent({});
        setHasChanges(false);
        setLoading(true);

        const options = {
            action: "getSleGroupData",
            data: groupInfoDto,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (groupFetchKeyRef.current !== fetchKey) return;
                const payload = response?.data?.payload || [];
                setGroupRows(payload);
                hydrateFromPayload(payload);
            })
            .catch((error) => {
                if (groupFetchKeyRef.current !== fetchKey) return;
                console.error("Failed to load group SLE ratings", error);
                toast.error("There was a problem loading School Level Expectation ratings. Please try again later.");
            })
            .finally(() => {
                if (groupFetchKeyRef.current === fetchKey) {
                    setLoading(false);
                }
            });
    }, [enabled, groupInfoDto, token, hydrateFromPayload]);

    const onChangeSleRating = useCallback((gradingProfileStudentId, uiKey, value) => {
        setValuesByStudent((prev) => {
            const gpsId = Number(gradingProfileStudentId);
            const prevMap = prev[gpsId] || {};
            return {
                ...prev,
                [gpsId]: {
                    ...prevMap,
                    [uiKey]: value || null // null is a delete
                }
            };
        });
        setHasChanges(true);
    }, []);

    const onChangeSleComment = useCallback((gradingProfileStudentId, comment) => {
        const gpsId = Number(gradingProfileStudentId);
        setSleCommentsByStudent((prev) => ({
            ...prev,
            [gpsId]: {
                ...(prev[gpsId] || { key: null }),
                comment
            }
        }));
        setHasChanges(true);
    }, []);

    // Helper: find existing rating row key for a given student/sleId/sleChildId across all groups
    const findExistingRatingKey = useCallback(
        (gradingProfileStudentId, sleId, sleChildId) => {
            const gpsId = Number(gradingProfileStudentId);

            for (const group of groupRows || []) {
                const student = (group.groupStudentSles || []).find((s) => Number(s.gradingProfileStudentId) === gpsId);
                if (!student) continue;

                const match = (student.sleRatings || []).find(
                    (r) =>
                        Number(r.sleId) === Number(sleId) &&
                        ((sleChildId == null && (r.sleChildId == null || r.sleChildId === 0)) ||
                            Number(r.sleChildId) === Number(sleChildId))
                );
                if (match) return Number(match.key) || null;
            }

            return null;
        },
        [groupRows]
    );

    // Flatten all students in the selected groups (dedupe by gradingProfileStudentId)
    const allStudentsById = useMemo(() => {
        const map = new Map();
        for (const group of groupRows || []) {
            for (const student of group.groupStudentSles || []) {
                const gpsId = Number(student.gradingProfileStudentId);
                if (!map.has(gpsId)) {
                    map.set(gpsId, student);
                }
            }
        }
        return map;
    }, [groupRows]);

    const buildRatingsPayload = useCallback(() => {
        const result = [];

        allStudentsById.forEach((student, gpsId) => {
            const existingRatings = [];
            for (const group of groupRows || []) {
                for (const s of group.groupStudentSles || []) {
                    if (Number(s.gradingProfileStudentId) === gpsId) {
                        existingRatings.push(...(s.sleRatings || []));
                    }
                }
            }

            const valueMap = valuesByStudent[gpsId] || {};

            // All UI keys for this student (from UI + existing)
            const allUiKeys = new Set([
                ...Object.keys(valueMap),
                ...existingRatings.map((r) =>
                    r.sleChildId == null ? makeSleKey(r.sleId) : makeSleChildKey(r.sleChildId)
                )
            ]);

            for (const uiKey of allUiKeys) {
                const { sleId, sleChildId } = parseSleUiKey(uiKey);

                if (!sleId && !sleChildId) continue;

                let uiVal;
                if (Object.prototype.hasOwnProperty.call(valueMap, uiKey)) {
                    uiVal = valueMap[uiKey];
                } else {
                    const match = existingRatings.find((r) =>
                        r.sleChildId == null ? uiKey === makeSleKey(r.sleId) : uiKey === makeSleChildKey(r.sleChildId)
                    );
                    uiVal = match ? normalizeSleRatingForUi(match.rating) : undefined;
                }

                if (uiVal === undefined) continue;

                let finalSleId = sleId;
                if (!finalSleId && sleChildId) {
                    finalSleId = childToParentMap[sleChildId];
                }
                if (!finalSleId) continue;

                const existingKey = findExistingRatingKey(gpsId, finalSleId, sleChildId || null);

                result.push({
                    key: existingKey,
                    sleId: Number(finalSleId),
                    sleChildId: sleChildId ? Number(sleChildId) : null,
                    gradingProfileStudentId: gpsId,
                    rating: normalizeSleRatingForApi(uiVal), // null → delete
                    personalizedLearningPlan: false,
                    lastRaterGuid: userGuid
                });
            }
        });

        return result;
    }, [allStudentsById, childToParentMap, findExistingRatingKey, groupRows, userGuid, valuesByStudent]);

    const saveGroupSleComments = useCallback(async () => {
        if (!enabled || !groupInfoDto || !token) return;

        const savedForKey = JSON.stringify(groupInfoDto);
        const entries = Object.entries(sleCommentsByStudent || {});
        if (!entries.length) return;

        const sleRatingComments = entries.map(([gpsId, { key, comment }]) => {
            const numericGpsId = Number(gpsId);

            // If there is an existing key and the comment is cleared out,
            // send null to indicate delete.
            const normalizedComment = key != null && (comment === "" || comment == null) ? null : (comment ?? "");

            return {
                key: key != null ? String(key) : null, // backend example shows string keys; adjust if needed
                gradingProfileStudentId: numericGpsId,
                comment: normalizedComment,
                lastUpdaterGuid: userGuid
            };
        });

        const options = {
            action: "updateSleRatingCommentsGroup",
            data: {
                groupInfoDto,
                sleRatingComments
            },
            token
        };

        try {
            const res = await ElementaryProgressReportDao(options);
            // Group switch while save in flight: do not apply the prior selection onto the new one.
            if (groupFetchKeyRef.current !== savedForKey) return;

            const payload = res?.data?.payload || [];

            const map = {};
            for (const row of payload) {
                const gpsId = Number(row.gradingProfileStudentId);
                map[gpsId] = {
                    key: row.key != null ? row.key : null,
                    comment: row.comment ?? ""
                };
            }

            setSleCommentsByStudent(map);
        } catch (error) {
            if (groupFetchKeyRef.current !== savedForKey) return;
            console.error("Failed to save group SLE comments", error);
            toast.error("Comments were not saved. Please make note of your comments and try again later.");
            throw error;
        }
    }, [enabled, groupInfoDto, sleCommentsByStudent, token, userGuid]);

    const saveGroupSleRatings = useCallback(async () => {
        if (!enabled || !groupInfoDto || !token) return;

        if (saveInFlightRef.current) {
            return saveInFlightRef.current;
        }

        const run = (async () => {
            const savedForKey = JSON.stringify(groupInfoDto);
            const sleRatings = buildRatingsPayload();
            const hasRatings = sleRatings.length > 0;

            let ratingOptions;

            if (hasRatings) {
                ratingOptions = {
                    action: "updateSleGroupData",
                    data: {
                        groupInfoDto,
                        sleRatings
                    },
                    token
                };
            }

            try {
                setLoading(true);

                let ratingsRes = null;

                if (ratingOptions) {
                    ratingsRes = await ElementaryProgressReportDao(ratingOptions);
                }

                await saveGroupSleComments();

                // Group switch while save in flight: request may have hit the server for the
                // prior selection, but must not rehydrate that payload onto the new selection.
                if (groupFetchKeyRef.current !== savedForKey) return;

                if (ratingsRes) {
                    const payload = ratingsRes?.data?.payload || [];
                    setGroupRows(payload);
                    // Ratings payload is from before/alongside the comment PUT and still
                    // has the old studentSleCommentDto — do not overwrite saved comments.
                    hydrateFromPayload(payload, { includeComments: false });
                }

                setHasChanges(false);
                toast.success("School Level Expectation ratings and comments successfully saved.");
            } catch (error) {
                if (groupFetchKeyRef.current !== savedForKey) return;
                console.error("Failed to save group SLE ratings/comments", error);
                toast.error("There was a problem saving School Level Expectation ratings. Please try again later.");
            } finally {
                if (groupFetchKeyRef.current === savedForKey) {
                    setLoading(false);
                }
            }
        })();

        saveInFlightRef.current = run;
        try {
            return await run;
        } finally {
            saveInFlightRef.current = null;
        }
    }, [buildRatingsPayload, enabled, groupInfoDto, hydrateFromPayload, saveGroupSleComments, token]);

    return {
        hasChanges,
        loading,
        groupRows,
        schoolLevelExpectations,
        sleCommentsByStudent,
        valuesByStudent,
        onChangeSleRating,
        onChangeSleComment,
        saveGroupSleRatings
    };
}
